import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Avatar,
  Typography
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { fetchUsers, deleteUser, updateUser, resetUserPassword, fetchUserById } from '../usersApi';

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit Modal State
  const [editOpen, setEditOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    Id: '',
    FullName: '',
    Phone: '',
    Email: ''
  });

  // Reset Password Modal State
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    Id: '',
    NewPassword: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await fetchUsers();
      // Filter out drivers as per original logic
      const filteredUsers = data.filter(u => u.role !== 'Driver' && u.Role !== 'Driver');
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      Swal.fire(t('error'), t('loadUsersError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('areYouSure'),
      text: t('revertWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('yesDeleteIt'),
      cancelButtonText: t('cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteUser(id);
          Swal.fire(t('deleted'), t('userDeleted'), 'success');
          loadUsers();
        } catch (error) {
          Swal.fire(t('error'), t('deleteUserError'), 'error');
        }
      }
    });
  };

  const handleEditClick = async (id) => {
    try {
      const user = await fetchUserById(id);
      setCurrentUser({
        Id: user.id || user.Id,
        FullName: user.fullName || user.FullName || '',
        Phone: user.phone || user.Phone || '',
        Email: user.email || user.Email || ''
      });
      setEditOpen(true);
    } catch (error) {
      Swal.fire(t('error'), t('loadUserDetailsError'), 'error');
    }
  };

  const handleEditSave = async () => {
    try {
      const payload = {
        FullName: currentUser.FullName,
        Phone: currentUser.Phone,
        Email: currentUser.Email
      };
      await updateUser(currentUser.Id, payload);
      setEditOpen(false);
      Swal.fire(t('success'), t('userUpdatedSuccess'), 'success');
      loadUsers();
    } catch (error) {
      Swal.fire(t('error'), t('updateUserError'), 'error');
    }
  };

  const handleResetPasswordClick = (id) => {
    setResetPasswordData({ Id: id, NewPassword: '' });
    setResetPasswordOpen(true);
  };

  const handleResetPasswordSave = async () => {
    if (!resetPasswordData.NewPassword) {
      Swal.fire(t('error'), t('enterNewPasswordError'), 'error');
      return;
    }
    try {
      await resetUserPassword(resetPasswordData.Id, resetPasswordData.NewPassword);
      setResetPasswordOpen(false);
      Swal.fire(t('success'), t('passwordChangedSuccess'), 'success');
    } catch (error) {
      Swal.fire(t('error'), t('changePasswordError'), 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={t('manageUsers')}
          subheader={t('manageUsersSubtitle')}
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={loadUsers}
              sx={{ borderRadius: 50 }}
            >
              {t('refreshData')}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ minWidth: 650 }} aria-label="users table">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('fullName')}</TableCell>
                  <TableCell>{t('phone')}</TableCell>
                  <TableCell>{t('email')}</TableCell>
                  <TableCell align="center">{t('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow key={user.id || user.Id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', mr: 2, fontWeight: 'bold' }}>
                          {(user.fullName || user.FullName || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.fullName || user.FullName || '-'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            #{user.id || user.Id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.phone || user.Phone || '-'}</TableCell>
                    <TableCell>{user.email || user.Email || '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleEditClick(user.id || user.Id)} title={t('edit')}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="warning" onClick={() => handleResetPasswordClick(user.id || user.Id)} title={t('changePassword')}>
                        <LockResetIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(user.id || user.Id)} title={t('delete')}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {t('noUsersFound')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: 'grey.100', color: 'primary.main', fontWeight: 'bold' }}>
          {t('editUserData')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('fullName')}
              fullWidth
              value={currentUser.FullName}
              onChange={(e) => setCurrentUser({ ...currentUser, FullName: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('phone')}
              fullWidth
              value={currentUser.Phone}
              onChange={(e) => setCurrentUser({ ...currentUser, Phone: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label={t('email')}
              fullWidth
              value={currentUser.Email}
              onChange={(e) => setCurrentUser({ ...currentUser, Email: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.50', p: 2 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" color="secondary" sx={{ borderRadius: 50 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleEditSave} variant="contained" color="primary" sx={{ borderRadius: 50 }}>
            {t('saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordOpen} onClose={() => setResetPasswordOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', fontWeight: 'bold' }}>
          {t('resetPassword')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <TextField
              label={t('newPassword')}
              type="password"
              fullWidth
              value={resetPasswordData.NewPassword}
              onChange={(e) => setResetPasswordData({ ...resetPasswordData, NewPassword: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder={t('enterNewPassword')}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'grey.50', p: 2 }}>
          <Button onClick={() => setResetPasswordOpen(false)} variant="outlined" color="secondary" sx={{ borderRadius: 50 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleResetPasswordSave} variant="contained" color="warning" sx={{ borderRadius: 50 }}>
            {t('changePassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
